//Login.js
import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Grid, Alert } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./Layout";
import { login, verifyMfa } from "../actions/authenActions";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State for login form
  const [formData, setFormData] = useState({
    user_id: "",
    password: "",
  });

  // State for MFA
  const [showMfaInput, setShowMfaInput] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaHash, setMfaHash] = useState("");
  const [mfaError, setMfaError] = useState("");

  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(null);

  const loggedInUser = useSelector((state) => state.authen.loggedInUser);

  useEffect(() => {
    if (loggedInUser) {
      if (loggedInUser.status === "Pass") {
        navigate("/dashboard");
      } else if (loggedInUser.status === "MFA_REQUIRED") {
        // Show MFA input form
        setShowMfaInput(true);
        setMfaHash(loggedInUser.mfa_hash);
        setSubmissionError(null);
      } else {
        setSubmissionError(
          loggedInUser.message || loggedInUser.status || "Login failed",
        );
      }
    }
  }, [loggedInUser, navigate]);

  function validate() {
    const errors = {};
    if (!formData.user_id) {
      errors.user_id = "Username is required";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    }
    return errors;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setSubmissionError(null);
  }

  function handleMfaChange(e) {
    setMfaCode(e.target.value);
    setMfaError("");
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    dispatch(login(formData))
      .then(() => {
        // handled by useEffect
      })
      .catch((error) => {
        setSubmissionError(error?.message || "An unexpected error occurred");
      });
  }

  function handleMfaSubmit(e) {
    e.preventDefault();

    if (!mfaCode || mfaCode.length !== 6) {
      setMfaError("Please enter a valid 6-digit MFA code");
      return;
    }

    const mfaData = {
      user_id: formData.user_id,
      mfa_code: mfaCode,
      mfa_hash: mfaHash,
    };

    dispatch(verifyMfa(mfaData))
      .then(() => {
        // handled by useEffect
      })
      .catch((error) => {
        setMfaError(error?.message || "Invalid MFA code");
      });
  }

  function handleBackToLogin() {
    setShowMfaInput(false);
    setMfaCode("");
    setMfaHash("");
    setMfaError("");
    setFormData({ user_id: "", password: "" });
  }

  // MFA Input Form
  if (showMfaInput) {
    return (
      <Layout>
        <Typography variant="h5" gutterBottom>
          Enter MFA Code
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          A verification code has been sent to your email. Please enter it
          below.
        </Typography>
        <Box
          component="form"
          onSubmit={handleMfaSubmit}
          noValidate
          autoComplete="off"
          sx={{ mt: 2 }}
        >
          {mfaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {mfaError}
            </Alert>
          )}
          <TextField
            label="MFA Code"
            name="mfa_code"
            fullWidth
            margin="normal"
            value={mfaCode}
            onChange={handleMfaChange}
            error={!!mfaError}
            helperText="Enter the 6-digit code from your email"
            inputProps={{ maxLength: 6, pattern: "[0-9]*" }}
          />
          <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
            Verify Code
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleBackToLogin}
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
        </Box>
      </Layout>
    );
  }

  // Login Form
  return (
    <>
      <Layout>
        <Typography variant="h5" gutterBottom>
          Login
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          sx={{ mt: 2 }}
        >
          {submissionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submissionError}
            </Alert>
          )}
          <TextField
            label="Username"
            name="user_id"
            fullWidth
            margin="normal"
            value={formData.user_id}
            onChange={handleChange}
            error={!!formErrors.user_id}
            helperText={formErrors.user_id}
          />
          <TextField
            label="Password"
            type="password"
            name="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={handleChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
          />
          <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }}>
            Login
          </Button>
          <Grid container spacing={1} mt={2}>
            <Grid item xs={12}>
              <Link to="/register">New here? Sign Up</Link>
            </Grid>
            <Grid item xs={12}>
              <Link to="/forgot">Forgot Credentials</Link>
            </Grid>
            <Grid item xs={12}>
              <Link to="/change-password">Change Password?</Link>
            </Grid>
          </Grid>
        </Box>
      </Layout>
    </>
  );
};

export default Login;
